import { computed, inject, Injectable } from '@angular/core';
import { filter, firstValueFrom, map } from 'rxjs';
import { WebAppDesignTokensStateService } from './web-app-design-tokens-state.service.ts';
import { DesignTokenEventsRepository } from '@tokenbox/database';
import { WorkspaceHistoryService } from './workspace-history.service.ts';
import { DesignTokenEvent, DesignTokenEventId } from '@tokenbox/domain';

@Injectable({
  providedIn: 'root',
})
export class UndoRedoService {
  private designTokensStateService = inject(WebAppDesignTokensStateService);
  private designTokensHistoryService = inject(WorkspaceHistoryService);
  private designTokenEventsRepository = inject(DesignTokenEventsRepository);

  private historyOfMyEvents = computed(() =>
    this.designTokensHistoryService.historyOfMyEvents().map((event) => ({
      ...event,
      summary: (() => {
        switch (event.type as DesignTokenEvent['type']) {
          case 'CREATE_THEMES':
            return 'Create theme(s)';
          case 'UPDATE_THEMES':
            return 'Update theme(s)';
          case 'DELETE_THEMES':
            return 'Delete theme(s)';
          case 'CREATE_TOKENS':
            return 'Create token(s)';
          case 'RENAME_TOKENS':
            return 'Rename token(s)';
          case 'UPDATE_VALUES':
            return 'Update token value(s)';
          case 'UPDATE_REFERENCED_TOKEN_NAMES':
            return 'Update reference(s)';
          case 'DELETE_TOKENS':
            return 'Delete token(s)';
          case 'NOOP':
          default:
            return undefined;
        }
      })(),
    })),
  );

  undoableEvents = computed(() =>
    this.historyOfMyEvents()
      .filter((event) => !event.undone)
      .toReversed(),
  );

  redoableEvents = computed(() =>
    this.historyOfMyEvents()
      .filter((event) => event.undone)
      .toReversed(),
  );

  nextUndoableEvent = computed(() => this.undoableEvents().at(-1));

  nextRedoableEvent = computed(() => this.redoableEvents().at(0));

  numberOfUndoableEvents = computed(() => this.undoableEvents().length);
  numberOfRedoableEvents = computed(() => this.redoableEvents().length);

  undoIsPossible = computed(() => this.numberOfUndoableEvents() > 0);
  redoIsPossible = computed(() => this.numberOfRedoableEvents() > 0);

  async undo() {
    const nextUndoableEvent = this.nextUndoableEvent();
    if (!nextUndoableEvent) {
      return;
    }
    return this.undoEvent(nextUndoableEvent.id);
  }

  async redo() {
    const nextRedoableEvent = this.nextRedoableEvent();
    if (!nextRedoableEvent) {
      return;
    }
    return this.redoEvent(nextRedoableEvent.id);
  }

  async undoEvent(eventId: DesignTokenEventId) {
    const update = this.designTokenEventsRepository.update(eventId, {
      undone: true,
    });
    const undoneEvent = this.waitForEventToHaveUndoneStatus(eventId, true);
    await update;
    return await undoneEvent;
  }

  async redoEvent(eventId: DesignTokenEventId) {
    const update = this.designTokenEventsRepository.update(eventId, {
      undone: false,
    });
    const redoneEvent = this.waitForEventToHaveUndoneStatus(eventId, false);
    await update;
    return await redoneEvent;
  }

  async setCurrentStateToSpecificEvent(eventId: string, createdAt: string) {
    const { branch } = await firstValueFrom(
      this.designTokensStateService.designTokensState$,
    );
    await this.designTokenEventsRepository.setCurrentStateToSpecificEvent(
      branch.id,
      eventId,
      createdAt,
    );
  }

  private async waitForEventToHaveUndoneStatus(
    eventId: string,
    undone: boolean,
  ): Promise<DesignTokenEvent | undefined> {
    return await firstValueFrom(
      this.designTokensStateService.events$.pipe(
        map((events) => events.find((event) => event.id === eventId)),
        filter((event) => {
          if (!event) {
            // if event was deleted -> continue
            return true;
          }
          return event.undone === undone;
        }),
      ),
    );
  }
}
